import { useNotificationModal } from '@room-match/shared-ui-components';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { ClearButton, Drop, DropCont, MainCont } from './DropZone.style';
import UploadingItem from './UploadingItem';
import { COLORS } from 'shared/constants/COLORS';
const { PRIMARY_ICON } = COLORS;

interface Props {
  limitReached: boolean;
  limit: number;
  onDropImages: (images: File[]) => void;
}

const DropZone: React.FC<Props> = ({ limitReached, limit, onDropImages }) => {
  const queryClient = useQueryClient();
  const [toUpload, setToUpload] = useState<{ id: string; file: any }[]>([]);
  const [doneIds, setdoneIds] = useState<string[]>([]);
  const { handleOpen, isOpen, NotificationModal, handleSetMessage } = useNotificationModal({
    type: 'warning',
  });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop(acceptedFiles) {
      let size = 0;
      acceptedFiles.map((f) => (size = size + f.size / 1000000));

      if (size > limit) {
        handleSetMessage('Limit Reached', `File is too large for your current plan limit.`, 'OK');
        return handleOpen();
      }

      if (limitReached) {
        handleSetMessage('Limit Reached', `Maximum upload limit reached. Please upgrade your plan.`, 'OK');
        return handleOpen();
      }

      const mFiles = acceptedFiles.filter((f) => !f.type.includes('image')).map((f, c) => ({ id: uuidv4(), file: f }));
      const images = acceptedFiles.filter((f) => f.type.includes('image') || f.type === 'image/tiff').map((f, c) => f);

      setToUpload([...toUpload, ...mFiles]);
      if (images.length !== 0) {
        onDropImages(images);
      }
    },
  });

  const setToDone = async (id: string) => {
    await queryClient.invalidateQueries(['media']);
    setdoneIds((dID) => [...dID, id]);
  };

  const removeMe = (id: string) => setToUpload((tu) => tu.filter((f) => f.id !== id));

  const clearAll = () => {
    setToUpload((tu) => tu.filter((f) => doneIds.indexOf(f.id) === -1));
    setdoneIds([]);
  };

  return (
    <MainCont>
      {isOpen && <NotificationModal onClose={() => {}} />}
      <DropCont>
        <Drop {...getRootProps({ className: 'dropzone' })}>
          <input {...getInputProps()} />
          <AiOutlineCloudUpload style={{ width: 72, height: 72, color: PRIMARY_ICON }} />
          <p style={{ marginTop: 10, marginBottom: 16, fontSize: 18 }}>Upload Media Here</p>
          <p style={{ marginBottom: 16, width: '80%', fontSize: 15 }}>
            Drag & Drop your images, videos, audio and document files anywhere on this area to begin!
          </p>
          <p style={{ marginBottom: 16, width: '60%', fontSize: 13 }}>
            Please ensure your media is high quality (images at least 400px in height and width, videos at 720p or
            higher)
          </p>
          <p style={{ marginBottom: 16, width: '80%', fontSize: 15 }}>
            Note: Each Video/Audio file uploaded must not exceed 10 minutes.
          </p>
        </Drop>
      </DropCont>
      <ClearButton disabled={!doneIds.length} onClick={clearAll}>
        Clear All Completed Uploads
      </ClearButton>
      {toUpload &&
        toUpload.map((f) => {
          const { file } = f;
          let type: 'Images' | 'Videos' | 'Audio' | 'Files' = 'Files';
          switch (true) {
            case file.type?.includes('image') || file.file_type?.includes('image'):
              type = 'Images';
              break;
            case file.type.includes('video'):
              type = 'Videos';
              break;
            case file.type.includes('audio'):
              type = 'Audio';
              break;
            default:
              type = 'Files';
          }
          return (
            <UploadingItem
              file={file}
              label={file.name || file.file_name}
              type={type}
              key={f.id}
              setToDone={setToDone}
              id={f.id}
              removeMe={removeMe}
            />
          );
        })}
    </MainCont>
  );
};

export default DropZone;
